import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MessageCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCompare } from "../contexts/CompareContext";
import { productById } from "../data/products";
import { hasVideo } from "../data/videoMap";
import { generateTelegramLink } from "../data/telegramLinks";

const ROWS = [
  { key: "cmpBestFor", get: (p, content) => content?.bestFor?.[0] || "—" },
  { key: "cmpLevel", get: (p) => p.level.join(", ") },
  { key: "cmpEnv", get: (p) => p.environment },
  { key: "cmpFormat", get: (p) => p.format.join(" · ") },
  { key: "cmpPlatform", get: (p) => p.categoryLabel },
  { key: "cmpManual", get: (p) => (p.format.some((f) => f.toLowerCase().includes("manual")) ? "yes" : "no"), bool: true },
  { key: "cmpBeginner", get: (p) => (p.level.includes("Beginner") || p.level.includes("All") ? "yes" : "no"), bool: true },
  { key: "cmpVideo", get: (p) => (hasVideo(p.id) ? "yes" : "no"), bool: true },
  { key: "cmpSupport", get: () => "yes", bool: true },
  { key: "cmpEducation", get: () => "yes", bool: true },
  { key: "cmpRisk", get: (p) => (p.id === "quant-research-algorithm" || p.id === "vertex-mt5-lab" || p.id === "mirror-study-access" ? "yes" : "no"), bool: true },
];

export const CompareDrawer = ({ onOpen }) => {
  const { t, productContent } = useLanguage();
  const { items, remove, clear } = useCompare();
  const [expanded, setExpanded] = React.useState(false);

  const productList = items.map((id) => productById(id)).filter(Boolean);

  React.useEffect(() => {
    if (expanded) {
      const onKey = (e) => e.key === "Escape" && setExpanded(false);
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [expanded]);

  return (
    <>
      {/* Floating compact bar */}
      <AnimatePresence>
        {items.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-5 right-5 z-40 hidden md:block"
            data-testid="compare-bar"
          >
            <div className="glass-strong rounded-full pl-2 pr-5 py-2 flex items-center gap-3 border border-[#D7B56D]/40">
              <div className="flex -space-x-2">
                {productList.slice(0, 3).map((p) => (
                  <div
                    key={p.id}
                    className="w-9 h-9 rounded-full border-2 border-[#1A1C22] flex items-center justify-center font-mono text-[10px] font-bold"
                    style={{ background: "#1C2633", color: "#D7B56D" }}
                  >
                    {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </div>
                ))}
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#D7B56D]">
                  {t("cmpTitle")}
                </div>
                <div className="text-[12px] text-[#F5F1EA]">{items.length} / 3</div>
              </div>
              <button
                onClick={() => setExpanded(true)}
                className="ml-2 px-4 py-2 rounded-full bg-[#D7B56D] text-[#0a0b0f] font-bold text-[11px] tracking-tight hover:bg-[#F5F1EA] transition-colors"
                data-testid="compare-open-btn"
              >
                Open
              </button>
              <button
                onClick={clear}
                className="text-[#D8DCE2]/60 hover:text-[#D7B56D] text-lg leading-none"
                aria-label={t("cmpClear")}
                data-testid="compare-clear-btn"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
            data-testid="compare-modal"
          >
            <div className="absolute inset-0 bg-[#07090D]/90 backdrop-blur-md" onClick={() => setExpanded(false)} />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className="relative w-full max-w-6xl glass-strong rounded-3xl p-6 md:p-9 my-10"
            >
              <div className="flex items-start justify-between mb-7">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#A9824A] mb-2">
                    · Compare
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F5F1EA]">{t("cmpTitle")}</h3>
                  <p className="text-[13px] text-[#D8DCE2]/65 mt-2">{t("cmpSub")}</p>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="w-10 h-10 rounded-full bg-[#1A1C22] border border-[#D8DCE2]/15 flex items-center justify-center hover:border-[#D7B56D]/40 transition-colors"
                  data-testid="compare-close-btn"
                  aria-label={t("close")}
                >
                  <X size={16} className="text-[#F5F1EA]" />
                </button>
              </div>

              {productList.length === 0 ? (
                <p className="py-12 text-center text-[#D8DCE2]/55">{t("cmpEmpty")}</p>
              ) : (
                <div className={`grid gap-4 grid-cols-1 ${productList.length >= 2 ? "md:grid-cols-2" : ""} ${productList.length >= 3 ? "lg:grid-cols-3" : ""}`}>
                  {productList.map((p) => {
                    const content = productContent(p.id);
                    return (
                      <div
                        key={p.id}
                        className="rounded-2xl border border-[#D8DCE2]/8 bg-[#1A1C22] p-6"
                        data-testid={`compare-card-${p.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A9824A] mb-1">
                              {p.categoryLabel}
                            </div>
                            <h4 className="font-display text-lg font-bold text-[#F5F1EA] leading-tight">
                              {p.name}
                            </h4>
                          </div>
                          <button
                            onClick={() => remove(p.id)}
                            className="text-[#D8DCE2]/60 hover:text-[#D7B56D]"
                            aria-label={t("cardRemove")}
                            data-testid={`compare-remove-${p.id}`}
                          >
                            <X size={14} />
                          </button>
                        </div>

                        <div className="space-y-3 mb-5">
                          {ROWS.map((row) => {
                            const value = row.get(p, content);
                            return (
                              <div key={row.key} className="flex items-start justify-between gap-3 pb-3 border-b border-[#D8DCE2]/8 text-[12px]">
                                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#A9824A] shrink-0">
                                  {t(row.key)}
                                </span>
                                <span className="text-[#E6E0D6] text-end">
                                  {row.bool ? t(value) : value}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              setExpanded(false);
                              onOpen(p.id);
                            }}
                            className="btn-ghost !py-2 text-[11px]"
                            data-testid={`compare-details-${p.id}`}
                          >
                            {t("cardOpenDetails")} <ArrowRight size={12} />
                          </button>
                          <a
                            href={generateTelegramLink(p.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gold !py-2 text-[11px]"
                            data-testid={`compare-cta-${p.id}`}
                          >
                            <MessageCircle size={12} /> {t("cardRequestAccess")}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
